import React from 'react';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';

const About = () => {
  return (
    <Layout>
      {/* Hero Section - Outlaw Souls */}
      <div className="bg-black py-12">
        <div className="container mx-auto px-4">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-6 text-center">
            Outlaw Souls
          </h1>
          <p className="text-light max-w-4xl mx-auto text-center text-lg italic">
            "Born in Bali—where incense smoke dances with engine oil and sunsets feel like sermons—Kala Hats is not just a brand. It's a flag for those who live by their own rules."
          </p>
        </div>
      </div>
      
      {/* SASTRAJIWA - The Artist */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0 md:pr-12">
              <h2 className="font-serif text-3xl font-bold text-primary mb-4">
                SASTRAJIWA
              </h2>
              <p className="text-primary text-lg font-semibold mb-4">
                Artist. Musician. Storyteller.
              </p>
              <p className="text-light mb-4">
                Kala Hats was founded by <strong>Sastrajiwa</strong>—an Indonesian artist, musician, and storyteller who channels shadow culture into wearable rebellion. Born on the mystical island of Bali, where ancient traditions dance with modern chaos, he discovered his calling in transforming the raw energy of resistance into tangible art.
              </p>
              <p className="text-light mb-4">
                For Sastrajiwa, Kala is not just a brand—it's <em>resistance</em>. A <em>living artwork</em>. A <em>ritual of identity</em> in a world obsessed with conformity.
              </p>
              <p className="text-light">
                Every piece he creates carries the soul of Bali's shadow culture—that darker, deeper world where rebels find their voice and outcasts become kings.
              </p>
            </div>
            <div className="md:w-1/2">
              <img 
                src="/images/boss.jpg" 
                alt="Sastrajiwa - Founder of Kala Hats" 
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Wearable Rebellion Philosophy */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl font-bold text-primary mb-6">
            Wearable Artifacts of a Darker, Deeper World
          </h2>
          <p className="text-light max-w-3xl mx-auto text-lg mb-8">
            We craft handmade hats, cowboy boots, and one-of-a-kind shirts as wearable artifacts of a darker, deeper world. Each piece is limited, meaningful, and unapologetically bold—created for modern outlaws, urban mystics, lone riders, and seekers of untamed beauty.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-secondary p-6 rounded-lg shadow-md border border-gray-700">
              <div className="text-primary text-4xl mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM7 21h10a2 2 0 002-2v-4a2 2 0 00-2-2H7M7 21V11H5a2 2 0 00-2 2v4a4 4 0 004 4z" />
                </svg>
              </div>
              <h3 className="font-serif text-xl font-bold mb-2 text-primary">Every Hat Carries a Message</h3>
              <p className="text-light">
                Crowns for modern rebels, each hat whispers secrets of resistance and speaks the language of the untamed soul.
              </p>
            </div>
            <div className="bg-secondary p-6 rounded-lg shadow-md border border-gray-700">
              <div className="text-primary text-4xl mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="font-serif text-xl font-bold mb-2 text-primary">Every Boot Leaves a Trail</h3>
              <p className="text-light">
                Footsteps of rebellion on the path of self-discovery. Each pair tells the story of journeys taken by those who dare to walk alone.
              </p>
            </div>
            <div className="bg-secondary p-6 rounded-lg shadow-md border border-gray-700">
              <div className="text-primary text-4xl mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="font-serif text-xl font-bold mb-2 text-primary">Every Shirt Tells a Story</h3>
              <p className="text-light">
                Wearable narratives for artistic expression. Each garment becomes a chapter in your personal mythology of rebellion.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* The Outlaw Souls Community */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4">
          <h2 className="font-serif text-3xl font-bold text-primary mb-8 text-center">
            For the Outlaw Souls
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <h3 className="font-serif text-xl font-bold text-primary mb-4">Modern Outlaws</h3>
              <p className="text-light">
                Contemporary rebels who reject mass production and embrace individuality. You wear your rebel identity like a flag, choosing substance over trends and authenticity over acceptance.
              </p>
            </div>
            <div className="text-center">
              <h3 className="font-serif text-xl font-bold text-primary mb-4">Urban Mystics</h3>
              <p className="text-light">
                City dwellers seeking spirituality in the modern world. You blend ancient wisdom with contemporary aesthetics, understanding the hidden symbols that speak to deeper truths.
              </p>
            </div>
            <div className="text-center">
              <h3 className="font-serif text-xl font-bold text-primary mb-4">Lone Riders</h3>
              <p className="text-light">
                Independent spirits who forge your own path. You value quality craftsmanship over quantity, choosing pieces that tell your story rather than follow the crowd.
              </p>
            </div>
            <div className="text-center">
              <h3 className="font-serif text-xl font-bold text-primary mb-4">Seekers of Untamed Beauty</h3>
              <p className="text-light">
                Drawn to raw, authentic aesthetics that can't be mass-produced. You find beauty in unexpected places and value the handmade over the machine-made.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Bali - Where Shadow Culture Meets Light */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2">
              <div className="grid grid-cols-2 gap-4">
                {[1, 2, 3, 4].map(num => (
                  <div key={num} className="rounded-lg overflow-hidden">
                    <img 
                      src={`/images/workshop-${num}.jpg`} 
                      alt={`Bali Workshop ${num}`} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="md:w-1/2 mt-8 md:mt-0 md:pl-12">
              <h2 className="font-serif text-3xl font-bold text-primary mb-4">
                Born in Bali
              </h2>
              <p className="text-light mb-4">
                In Canggu, Bali—where incense smoke dances with engine oil and sunsets feel like sermons—Sastrajiwa's workshop exists as a living artwork. Here, traditional Balinese craftsmanship meets the raw energy of modern rebellion.
              </p>
              <p className="text-light mb-4">
                This is where shadow culture takes physical form. Where ancient techniques serve contemporary outcasts. Where each piece begins its journey from concept to wearable manifesto.
              </p>
              <p className="text-light mb-6">
                Visitors to our Bali workshop don't just observe—they participate in the ritual of creation, witnessing the transformation of raw materials into artifacts of identity.
              </p>
              <Button to="/showroom" variant="secondary">
                Visit Our Sacred Workshop
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* The Resistance Begins */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl font-bold text-primary mb-4">
            The Resistance Begins with You
          </h2>
          <p className="text-light max-w-3xl mx-auto mb-8">
            By choosing Kala Hats, you're not just acquiring wearable art—you're joining a movement. A community of outlaw souls who refuse to conform, who wear their rebellion as armor, and who understand that true style is an act of resistance.
          </p>
          <blockquote className="text-primary italic text-xl max-w-2xl mx-auto mb-8">
            "In a world obsessed with conformity, wearing your authentic self becomes the ultimate rebellion."
          </blockquote>
          <div className="flex flex-wrap justify-center gap-4">
            <Button to="/products" size="lg">
              Explore Our Artifacts
            </Button>
            <Button to="/custom-order" variant="outline" size="lg">
              Create Your Ritual
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
